import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { useMe } from "../../../hooks/queries";
import { MediaData } from "../../../models/MediaData";

import "./styles.scss";

export interface IMediaItemProps {
  mediaItem: MediaData | null;
  hideLocationName?: boolean;
}

export const MediaItem: React.FunctionComponent<IMediaItemProps> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`mediaItem.${key}`);

  const { mediaItem, hideLocationName } = props;
  const { data: me } = useMe();

  const url = mediaItem?.url;

  // Validate the image URL by making a HEAD request to check if it's accessible.
  // This is necessary because GBP's image server returns a 403 status code with a fallback image
  // The fallback image prevents the <img> tag from triggering an error event, so we need to check the URL's validity beforehand.
  const { data: isValid } = useQuery<boolean>({
    queryKey: ["mediaItemImage", url],
    enabled: !!url,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      const response = await fetch(url as string, { method: "HEAD" });
      // Consider 403 and 404 status codes as indicators of an invalid image URL
      // Other errors (429 for too many requests, 500 for temporary issue on other side) we do not want to hide
      const isError = response.status === 403 || response.status === 404;
      return !isError;
    },
  });

  if (!mediaItem) return null;

  const locationPath = me?.organization_id
    ? `/location/${mediaItem.location_id}/organization/${me?.organization_id}`
    : `/location/${mediaItem.location_id}`;

  // If the image is invalid, hide the entire Media Item
  // Strictly check for false, not undefined
  if (isValid === false) {
    return null;
  }

  // If the image is valid, or validity is still loading, render the image component as normal
  // This allows the component to begin loading the image immediately, relying on the browser's default behavior,
  // then unmount if/when the validity check fails

  const postDate = mediaItem.post_date
    ? dayjs(mediaItem.post_date).format("YYYY-MM-DD")
    : t("noPostDate");

  return (
    <div className="media-item" key={mediaItem.id}>
      <div className="media-top-info">
        <div className="left-title">
          {!hideLocationName && (
            <NavLink to={locationPath}>{mediaItem.location_name}</NavLink>
          )}
        </div>
        <div className="right-title">{postDate}</div>
      </div>
      <a
        className="media-img-container"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          className="media-img"
          alt="media"
          src={url}
          referrerPolicy="no-referrer"
        />
      </a>
    </div>
  );
};

export default MediaItem;
